
import React from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { useUserRole } from '@/hooks/useUserRole';
import WaitlistStats from '@/components/admin/WaitlistStats';
import WaitlistReminders from '@/components/admin/WaitlistReminders';
import FacebookAPITester from '@/components/admin/FacebookAPITester';
import TeamMemberManagement from '@/components/admin/TeamMemberManagement';
import { OAuthTester } from '@/components/admin/OAuthTester';
import { RedditCredentialsDiagnostic } from '@/components/admin/RedditCredentialsDiagnostic';
import { RedditConnectivityTester } from '@/components/admin/RedditConnectivityTester';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ShieldAlert } from 'lucide-react';

const AdminDashboard = () => {
  const { user, loading: authLoading } = useAuth();
  const { isAdmin, loading: roleLoading } = useUserRole();
  const navigate = useNavigate();

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/auth');
      return;
    }
  }, [user, authLoading, navigate]);

  const handleLogoClick = () => {
    navigate('/');
  };

  if (authLoading || roleLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Check if user has admin access
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <header className="bg-card border-b border-border shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <button 
                onClick={handleLogoClick}
                className="flex items-center space-x-2 hover:opacity-80 transition-opacity cursor-pointer"
              >
                <div className="w-8 h-8 bg-gradient-to-br from-green-600 to-emerald-700 rounded-sm rotate-45"></div>
                <span className="text-2xl font-bold text-foreground tracking-tight hover:text-primary transition-colors">SocialScoop.ai</span>
              </button>
              <div className="flex items-center space-x-4">
                <Button variant="outline" onClick={() => navigate('/dashboard')}>
                  Back to Dashboard
                </Button>
              </div>
            </div>
          </div>
        </header>

        {/* Access Denied */}
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-center min-h-[400px]">
            <Card className="w-full max-w-md">
              <CardHeader className="text-center">
                <div className="mx-auto w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
                  <ShieldAlert className="h-6 w-6 text-red-600" />
                </div>
                <CardTitle className="text-red-600">Access Denied</CardTitle>
                <CardDescription>
                  You don't have administrator privileges to access this page.
                </CardDescription>
              </CardHeader>
              <CardContent className="text-center">
                <Button onClick={() => navigate('/dashboard')} className="w-full">
                  Return to Dashboard
                </Button>
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-card border-b border-border shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <button 
              onClick={handleLogoClick}
              className="flex items-center space-x-2 hover:opacity-80 transition-opacity cursor-pointer"
            >
              <div className="w-8 h-8 bg-gradient-to-br from-green-600 to-emerald-700 rounded-sm rotate-45"></div>
              <span className="text-2xl font-bold text-foreground tracking-tight hover:text-primary transition-colors">SocialScoop.ai</span>
            </button>
            <div className="flex items-center space-x-4">
              <span className="text-gray-600">Admin Dashboard</span>
              <Button variant="outline" onClick={() => navigate('/dashboard')}>
                Back to Dashboard
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
          <p className="text-gray-600">Monitor your waitlist and platform usage</p>
        </div>

        <div className="space-y-8">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Team Access Management</h2>
            <TeamMemberManagement />
          </div>
          
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Waitlist Statistics</h2>
            <WaitlistStats />
          </div>
          
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Email Management</h2>
            <WaitlistReminders />
          </div>

          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Reddit OAuth Diagnostics</h2>
            <div className="space-y-4">
              <RedditCredentialsDiagnostic />
              <RedditConnectivityTester />
            </div>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">OAuth Testing</h2>
            <OAuthTester />
          </div>

          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">API Testing</h2>
            <FacebookAPITester />
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
